import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * Reusable searchable multi-select dropdown.
 *
 * Options and selected items share the shape { id, name }. Selection stays as
 * full objects so callers can keep any extra metadata on the option. The
 * parent owns the selection state and receives changes via onSelect/onRemove.
 *
 * @param {Object[]} options    Full list of selectable items ({ id, name }).
 * @param {Object[]} selected   Currently selected items ({ id, name }).
 * @param {Function} onSelect   Called with the option object when picked.
 * @param {Function} onRemove   Called with an item's id when its chip is removed.
 * @param {string}   [label]    Field label rendered above the control.
 * @param {string}   [placeholder] Placeholder shown while nothing is selected.
 * @param {'alphabetics'} [sort] Optional. Sort the visible options A→Z by name.
 * @param {boolean}  [disabled] Disables the control.
 * @param {boolean}  [required] Appends a "*" to the label.
 */
const SearchableDropdown = ({
  options = [],
  selected = [],
  onSelect,
  onRemove,
  label = "",
  placeholder = "",
  sort,
  disabled = false,
  required = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const visibleOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? options.filter((option) =>
          option.name?.toLowerCase().includes(needle)
        )
      : options;
    if (sort === "alphabetics") {
      return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }
    return filtered;
  }, [options, query, sort]);

  const isSelected = (option) =>
    selected.some((item) => item.id === option.id);

  const handleSelect = (option) => {
    onSelect(option);
    setQuery("");
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-900">
          {label} {required && "*"}
        </label>
      )}

      <div
        className={`border p-3 bg-white text-gray-800 border-gray-300 shadow-md placeholder:text-base duration-300 rounded-lg w-full appearance-none ${
          disabled ? "bg-gray-400 pointer-events-none" : ""
        }`}
        onClick={() => !disabled && setIsOpen(true)}
      >
        <div className="flex flex-wrap items-center">
          {selected?.map((item) => (
            <div
              key={item.id}
              className="flex justify-center items-center m-1 font-medium py-1 px-2 rounded-full text-teal-700 bg-teal-100 border border-teal-300"
            >
              <span>{item.name}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item.id);
                }}
                className="text-red-600 ml-2"
              >
                &times;
              </button>
            </div>
          ))}
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={selected.length === 0 ? placeholder : ""}
            disabled={disabled}
            className="bg-transparent outline-none flex-1 cursor-text min-w-[6rem]"
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto border border-gray-300">
          {visibleOptions.length === 0 ? (
            <div className="px-3 py-2 bg-gray-100 text-gray-500">
              {t("noResults", "No results found")}
            </div>
          ) : (
            visibleOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`cursor-pointer px-3 py-2 bg-gray-100 hover:bg-teal-100 ${
                  isSelected(option) ? "text-teal-700" : "text-gray-800"
                }`}
              >
                {option.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

SearchableDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  sort: PropTypes.oneOf(["alphabetics"]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

export default SearchableDropdown;
